import type { IceWinReason, Side } from './types.js';

/**
 * A match is two games on the same lake with the sides swapped, so each player attacks once
 * (`docs/design-ice.md`, Match). It cancels most of any bias in the round limit.
 */

/** The two players of a match: who created it, and who joined. */
export type Seat = 'host' | 'guest';

export function otherSeat(seat: Seat): Seat {
  return seat === 'host' ? 'guest' : 'host';
}

/** The seat playing Brand in game `index` (0 or 1): the sides swap for the second game. */
export function brandSeat(firstBrand: Seat, index: number): Seat {
  return index % 2 === 0 ? firstBrand : otherSeat(firstBrand);
}

/** The side `seat` plays in game `index`. */
export function sideOf(firstBrand: Seat, index: number, seat: Seat): Side {
  return brandSeat(firstBrand, index) === seat ? 'brand' : 'rime';
}

/** How one game of the match ended, from the attacker's point of view. */
export interface IceMatchGame {
  /** The seat that played Brand. */
  attacker: Seat;
  winner: Side;
  reason: IceWinReason;
  /** Brand's Glory at the end. */
  glory: number;
  /** The round the game ended in. */
  round: number;
}

export type IceMatchReason =
  /** Both attacks succeeded: the faster one (then the one with more Glory) wins. */
  | 'faster'
  /** Both attacks failed: more Glory as attacker wins. */
  | 'glory'
  /** Only one attack succeeded. */
  | 'attack'
  /** A player resigned, which forfeits the match. */
  | 'resign'
  /** Nothing separates the two attacks: the match is drawn. */
  | 'level';

export interface IceMatchOutcome {
  winner: Seat | null;
  reason: IceMatchReason;
}

/**
 * The result of a match, or `null` while it is still going. A resignation in either game ends
 * the match at once; otherwise both games are needed.
 */
export function iceMatchOutcome(games: readonly IceMatchGame[]): IceMatchOutcome | null {
  for (const game of games) {
    if (game.reason === 'resign') {
      const loser = game.winner === 'brand' ? otherSeat(game.attacker) : game.attacker;
      return { winner: otherSeat(loser), reason: 'resign' };
    }
  }
  const [a, b] = games;
  if (!a || !b) return null;
  const aWon = a.winner === 'brand';
  const bWon = b.winner === 'brand';
  if (aWon !== bWon) return { winner: aWon ? a.attacker : b.attacker, reason: 'attack' };
  if (aWon) {
    if (a.round !== b.round)
      return { winner: a.round < b.round ? a.attacker : b.attacker, reason: 'faster' };
    if (a.glory !== b.glory)
      return { winner: a.glory > b.glory ? a.attacker : b.attacker, reason: 'faster' };
    return { winner: null, reason: 'level' };
  }
  if (a.glory !== b.glory)
    return { winner: a.glory > b.glory ? a.attacker : b.attacker, reason: 'glory' };
  return { winner: null, reason: 'level' };
}
